<template>
  <section class="ctc-product-results">
    <header class="ctc-product-results__header">
      <span>
        <strong>
          {{ products.length }} résultat{{ products.length > 1 ? "s" : "" }}
        </strong>
        <small>avec une offre disponible pour ce site</small>
      </span>
      <small v-if="pageCount > 1">Page {{ page }} sur {{ pageCount }}</small>
    </header>
    <ul
      class="ctc-product-grid"
      aria-label="Produits similaires trouvés par Affilizz"
    >
      <li
        v-for="product in paginatedProducts"
        :key="product.productLocalizedId"
        class="ctc-product-card"
        :class="{
          'ctc-product-card--selected': selectedIds.includes(
            product.productLocalizedId
          ),
        }"
      >
        <div class="ctc-product-card__media">
          <img
            v-if="product.imageUrl"
            :src="product.imageUrl"
            alt=""
            width="160"
            height="112"
            loading="lazy"
          />
          <v-icon v-else large :color="subtleColor" aria-hidden="true">
            {{ imageIcon }}
          </v-icon>
          <span class="ctc-product-card__source">Affilizz</span>
        </div>

        <div class="ctc-product-card__body">
          <h4 class="ctc-product-card__title" :title="product.title">
            {{ product.title }}
          </h4>
          <div class="ctc-product-card__details">
            <strong>{{ priceLabel(product) }}</strong>
            <small v-if="hasPriceRange(product)">
              Jusqu’à {{ formatPrice(product.maxPrice, product.currency) }}
            </small>
            <small>{{ offerLabel(product.offerCount) }} par Affilizz</small>
          </div>
          <div class="ctc-product-card__actions">
            <span
              v-if="selectedIds.includes(product.productLocalizedId)"
              class="ctc-product-card__selected"
            >
              Déjà ajouté au widget
            </span>
            <rmr-action-button
              v-else-if="canSelect"
              :label="actionLabel"
              :icon="plusIcon"
              size="xs"
              @click="$emit('select', product)"
            />
          </div>
        </div>
      </li>
    </ul>
    <rmr-pagination
      v-if="pageCount > 1"
      v-model="page"
      :length="pageCount"
      :total-visible="7"
      class="ctc-product-results__pagination"
      aria-label="Pages des résultats Affilizz"
    />
  </section>
</template>

<script>
import { mdiImageOffOutline, mdiPlus } from "@mdi/js";
import { computed, ref, watch } from "@vue/composition-api";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrPagination from "@/components/ui/actions/RmrPagination.vue";
import { RMR_TEXT_SUBTLE } from "@/styles/tokens";

import { formatPrice, offerCountLabel, similarPriceLabel } from "../ctc-model";

export default {
  name: "CtcSimilarProductCards",
  components: { RmrActionButton, RmrPagination },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    canSelect: {
      type: Boolean,
      default: false,
    },
    actionLabel: {
      type: String,
      default: "Ajouter ce produit",
    },
    pageSize: {
      type: Number,
      default: 6,
    },
  },
  setup(props) {
    const page = ref(1);
    const pageCount = computed(() =>
      Math.max(1, Math.ceil(props.products.length / props.pageSize))
    );
    watch(
      () => props.products,
      () => {
        page.value = 1;
      }
    );
    return {
      formatPrice,
      hasPriceRange: (product) =>
        typeof product.minPrice === "number" &&
        typeof product.maxPrice === "number" &&
        product.maxPrice > product.minPrice,
      imageIcon: mdiImageOffOutline,
      offerLabel: offerCountLabel,
      page,
      pageCount,
      paginatedProducts: computed(() => {
        const start = (page.value - 1) * props.pageSize;
        return props.products.slice(start, start + props.pageSize);
      }),
      plusIcon: mdiPlus,
      priceLabel: similarPriceLabel,
      subtleColor: RMR_TEXT_SUBTLE,
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-product-results {
  display: grid;
  gap: 14px;
}

.ctc-product-results__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  color: $rmr-text-primary;
}

.ctc-product-results__header strong,
.ctc-product-results__header small {
  display: block;
}

.ctc-product-results__header strong {
  font-size: $rmr-fs-body;
}

.ctc-product-results__header small {
  margin-top: 2px;
  color: $rmr-text-muted;
  font-size: $rmr-fs-sm;
}

.ctc-product-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ctc-product-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-xl;
  background: $rmr-surface-0;
  box-shadow: $rmr-shadow-card;
}

.ctc-product-card:hover {
  border-color: $rmr-indigo-border;
  box-shadow: $rmr-shadow-card-hover;
}

.ctc-product-card--selected {
  border-color: rgba($rmr-green, 0.32);
  background: $rmr-green-bg;
}

.ctc-product-card__media {
  position: relative;
  display: flex;
  height: 112px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-bottom: 1px solid $rmr-border;
  background: $rmr-surface-2;
}

.ctc-product-card__media img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ctc-product-card__source {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 7px;
  border-radius: $rmr-radius-pill;
  background: rgba($rmr-surface-0, 0.94);
  color: $rmr-indigo-dark;
  font-size: $rmr-fs-sm;
  font-weight: 800;
  line-height: 1;
  box-shadow: $rmr-shadow-card;
}

.ctc-product-card__body {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 11px 12px 12px;
}

.ctc-product-card__title {
  display: -webkit-box;
  overflow: hidden;
  min-height: 36px;
  margin: 0 0 8px;
  color: $rmr-text-primary;
  font-size: $rmr-fs-body;
  font-weight: 700;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.ctc-product-card__details {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 7px;
}

.ctc-product-card__details strong {
  width: 100%;
  color: $rmr-text-primary;
  font-size: $rmr-fs-title;
}

.ctc-product-card__details small {
  display: block;
  color: $rmr-text-muted;
  font-size: $rmr-fs-base;
}

.ctc-product-card__actions {
  display: flex;
  min-height: 38px;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
}

.ctc-product-card__selected {
  color: $rmr-green-dark;
  font-size: $rmr-fs-base;
  font-weight: 700;
}

.ctc-product-results__pagination {
  display: flex !important;
  align-items: center;
  justify-self: center;
  justify-content: center;
  gap: 6px;
  padding: 4px;
  border: 1px solid $rmr-border-hover;
  border-radius: $rmr-radius-lg;
  background: $rmr-surface-2;
}

.ctc-product-results__pagination ::v-deep .rmr-pagination__button {
  display: inline-flex !important;
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  align-items: center;
  justify-content: center;
  border: 1px solid $rmr-border-hover !important;
  border-radius: $rmr-radius-md !important;
  background: $rmr-surface-0 !important;
  color: $rmr-text-secondary !important;
  font-weight: 600;
}

.ctc-product-results__pagination ::v-deep .rmr-pagination__button--active {
  border-color: $rmr-indigo !important;
  background: $rmr-indigo !important;
  color: $rmr-surface-0 !important;
  box-shadow: 0 1px 3px rgba(49, 46, 129, 0.2);
}

.ctc-product-results__pagination ::v-deep .rmr-pagination__button:disabled {
  border-color: $rmr-border !important;
  background: $rmr-surface-1 !important;
  color: $rmr-text-subtle !important;
}

.ctc-product-results__pagination ::v-deep .rmr-pagination__ellipsis {
  min-width: 18px;
  color: $rmr-text-muted;
  text-align: center;
}

@media (max-width: 900px) {
  .ctc-product-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .ctc-product-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .ctc-product-card__media {
    height: 132px;
  }
}
</style>
